import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Mail, Printer, ChevronLeft, ChevronRight } from 'lucide-react'
import { LocalizedLink } from './LocalizedLink'
import { cn } from '@/lib/utils'

export function Footer() {
  const { t } = useTranslation()
  const [locationIndex, setLocationIndex] = useState(0)

  const mainLocation = {
    key: 'pravisdomini',
    name: t('locations.pravisdomini.name'),
    label: t('locations.pravisdomini.label'),
    address: t('locations.pravisdomini.address'),
    city: `${t('locations.pravisdomini.city')} — ${t('locations.pravisdomini.country')}`,
    phone: t('locations.pravisdomini.phone'),
  }

  const localUnits = [
    {
      key: 'sanVito',
      name: t('locations.sanVito.name'),
      label: t('locations.sanVito.label'),
      address: t('locations.sanVito.address'),
      city: `${t('locations.sanVito.city')} — ${t('locations.sanVito.country')}`,
      phone: t('locations.sanVito.phone'),
    },
    {
      key: 'porcia',
      name: t('locations.porcia.name'),
      label: t('locations.porcia.label'),
      address: t('locations.porcia.address'),
      city: `${t('locations.porcia.city')} — ${t('locations.porcia.country')}`,
      phone: t('locations.porcia.phone'),
    },
    {
      key: 'barletta',
      name: t('locations.barletta.name'),
      label: t('locations.barletta.label'),
      address: t('locations.barletta.address'),
      city: `${t('locations.barletta.city')} — ${t('locations.barletta.country')}`,
      phone: t('locations.barletta.phone'),
    },
    {
      key: 'catania',
      name: t('locations.catania.name'),
      label: t('locations.catania.label'),
      address: t('locations.catania.address'),
      city: `${t('locations.catania.city')} — ${t('locations.catania.country')}`,
      phone: t('locations.catania.phone') || undefined,
    },
  ]

  const currentLocation = localUnits[locationIndex]

  const prevLocation = () => setLocationIndex((i) => (i - 1 + localUnits.length) % localUnits.length)
  const nextLocation = () => setLocationIndex((i) => (i + 1) % localUnits.length)

  const contacts = [
    {
      icon: Phone,
      label: t('contacts.phone'),
      value: '+39 0434 645075',
      href: 'tel:+390434645075',
    },
    {
      icon: Printer,
      label: t('contacts.fax'),
      value: '+39 0434 645505',
      href: 'tel:+390434645505',
    },
    {
      icon: Mail,
      label: t('contacts.email'),
      value: 'info@fgautomazioni.it',
      href: 'mailto:info@fgautomazioni.it',
    },
    {
      icon: Mail,
      label: t('contacts.pec'),
      value: 'fgautomazioni@legalmail.it',
      href: 'mailto:fgautomazioni@legalmail.it',
    },
  ]

  const socials = [
    {
      name: 'LinkedIn',
      href: 'https://it.linkedin.com/company/fg-automazioni-s.r.l.',
    },
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/fgautomazioni',
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/fgautomazioni.srl',
    },
  ]

  return (
    <footer className="bg-gray-900 text-white">
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Where we are */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-white/80">
              {t('footer.whereWeAre')}
            </h2>

            {/* Main Location - Pravisdomini always visible */}
            <div className="space-y-1 border-b border-white/10 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                {mainLocation.label}
              </p>
              <p className="text-sm font-medium text-white">{mainLocation.name}</p>
              <div className="flex items-start gap-2 text-sm text-white/80">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#f75e47]" />
                <div>
                  <p>{mainLocation.address}</p>
                  <p>{mainLocation.city}</p>
                </div>
              </div>
              {mainLocation.phone && (
                <p className="flex items-center gap-2 text-sm text-white/80">
                  <Phone className="h-4 w-4 shrink-0 text-[#f75e47]" />
                  <a href={`tel:${mainLocation.phone.replace(/\s/g, '')}`} className="hover:text-white">
                    {mainLocation.phone}
                  </a>
                </p>
              )}
            </div>

            {/* Local Units Section */}
            <div className="space-y-2 pt-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                {t('locations.localUnits')}
              </p>

              {/* Location Switcher */}
              <div className="flex items-center gap-2">
                <button
                  onClick={prevLocation}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
                  aria-label={t('footerNavigation.prevLocation')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="min-w-0 flex-1 truncate text-center text-sm font-medium text-white">
                  {currentLocation.name}
                </span>
                <button
                  onClick={nextLocation}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
                  aria-label={t('footerNavigation.nextLocation')}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex justify-center gap-1 pt-1">
                {localUnits.map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'h-1.5 w-1.5 rounded-full transition-colors',
                      i === locationIndex ? 'bg-[#f75e47]' : 'bg-white/20'
                    )}
                  />
                ))}
              </div>

              {/* Selected Local Unit Details */}
              <div className="space-y-1 pt-2">
                <div className="flex items-start gap-2 text-sm text-white/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#f75e47]" />
                  <div>
                    <p>{currentLocation.address}</p>
                    <p>{currentLocation.city}</p>
                  </div>
                </div>
                <p className={cn('flex items-center gap-2 text-sm text-white/80', !currentLocation.phone && 'invisible')}>
                  <Phone className="h-4 w-4 shrink-0 text-[#f75e47]" />
                  {currentLocation.phone ? (
                    <a href={`tel:${currentLocation.phone.replace(/\s/g, '')}`} className="hover:text-white">
                      {currentLocation.phone}
                    </a>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-white/80">
              {t('footer.contacts')}
            </h2>
            <ul className="space-y-3">
              {contacts.map((c) => (
                <li key={c.label} className="flex items-start gap-3">
                  <c.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#f75e47]" />
                  <div>
                    <p className="text-xs text-white/60">{c.label}</p>
                    <a
                      href={c.href}
                      className="text-sm text-white/90 hover:text-white hover:underline"
                    >
                      {c.value}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal + Menu */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-white/80">
                {t('footer.legalNotes')}
              </h2>
              <ul className="space-y-2">
                <li>
                  <LocalizedLink
                    to="privacy"
                    className="text-sm text-white/80 hover:text-white hover:underline"
                  >
                    {t('footer.privacy')}
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink
                    to="cookies"
                    className="text-sm text-white/80 hover:text-white hover:underline"
                  >
                    {t('footer.cookiePolicy')}
                  </LocalizedLink>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-white/80">
                {t('footer.menu')}
              </h2>
              <ul className="space-y-2">
                <li>
                  <LocalizedLink
                    to="careers"
                    className="text-sm text-white/80 hover:text-white hover:underline"
                  >
                    {t('nav.careers')}
                  </LocalizedLink>
                </li>
              </ul>
            </div>
          </div>

          {/* Social */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-white/80">
              {t('footer.social')}
            </h2>
            <ul className="flex flex-wrap gap-3">
              {socials.map((s) => (
                <li key={s.name}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md bg-white/10 px-3 py-2 text-sm text-white/90 transition-colors hover:bg-white/20"
                  >
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
            <p className="text-xs text-white/50">
              © {new Date().getFullYear()} {t('company.name')} — {t('footer.allRightsReserved')}
            </p>
          </div>
        </div>
      </div>

      {/* Company legal info bar */}
      <div className="border-t border-white/10 bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 py-4 lg:px-8">
          <p className="text-center text-xs leading-relaxed text-white/40">
            {t('footerLegal.legalInfo')}
          </p>
        </div>
      </div>
    </footer>
  )
}
